import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, Play, Flame, Eye } from "lucide-react";
import api, { resolveAssetUrl, formatEpisodeNumber, formatDurationShort, formatViews, splitEpisodeTitle } from "../lib/api";

const AUTOPLAY_MS = 5000;
const SWIPE_THRESHOLD_PX = 40;

// Homepage "most viewed episodes" carousel.
// Constraints (user-specified):
//   - Constrained to the main container width (never full-width)
//   - At most one episode per series (backend already enforces this)
//   - Auto-advance every 5s, pause on hover
//   - Prev/Next arrows + bottom dot navigation
//   - Smooth translate transition + mobile swipe (pointer events)
export default function TopEpisodesCarousel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const trackRef = useRef(null);
  const pointerStartRef = useRef({ x: 0, active: false });

  useEffect(() => {
    let mounted = true;
    api.get("/episodes/most-viewed", { params: { limit: 5 } })
      .then((r) => { if (mounted) setItems(r.data || []); })
      .catch(() => { if (mounted) setItems([]); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, []);

  const count = items.length;

  // Auto-advance every AUTOPLAY_MS while not paused. Reset the timer every
  // time `idx` changes (manual arrow/dot clicks) so 5s applies from the last
  // interaction, not from mount.
  useEffect(() => {
    if (paused || count <= 1) return;
    const t = setTimeout(() => setIdx((n) => (n + 1) % count), AUTOPLAY_MS);
    return () => clearTimeout(t);
  }, [idx, paused, count]);

  const goPrev = () => setIdx((n) => (n - 1 + count) % count);
  const goNext = () => setIdx((n) => (n + 1) % count);

  // Pointer-based swipe. Uses pointer events so it works for both touch and
  // mouse-drag interactions on desktop track-pads without a separate handler.
  const onPointerDown = (e) => { pointerStartRef.current = { x: e.clientX, active: true }; };
  const onPointerUp = (e) => {
    const st = pointerStartRef.current;
    if (!st.active) return;
    pointerStartRef.current = { x: 0, active: false };
    const dx = e.clientX - st.x;
    if (Math.abs(dx) < SWIPE_THRESHOLD_PX) return;
    if (dx > 0) goPrev(); else goNext();
  };

  const dots = useMemo(() => Array.from({ length: count }, (_, i) => i), [count]);

  if (loading) {
    return (
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="top-episodes-carousel">
        <div className="h-72 border-4 border-[#ff2fa7]/40 rounded-3xl animate-pulse bg-[#0a0f0d]/60" />
      </section>
    );
  }
  if (count === 0) return null;

  return (
    <section
      className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-12 overflow-x-hidden"
      data-testid="top-episodes-carousel"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="flex items-end justify-between mb-4 sm:mb-6 flex-wrap gap-2 sm:gap-4">
        <h2 className="font-display text-2xl sm:text-4xl font-black text-slate-900 text-bubble-sm inline-flex items-center gap-2 sm:gap-3">
          <span className="inline-flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-[#ff2fa7] border-4 border-black shadow-brutal-sm">
            <Flame className="w-4 h-4 sm:w-5 sm:h-5 text-white" strokeWidth={3} />
          </span>
          Cele mai <span className="text-pink-500">urmărite</span>
        </h2>
        {count > 1 && (
          <span className="text-xs font-black uppercase tracking-widest text-[#00e5ff]" data-testid="carousel-position">
            {idx + 1} / {count}
          </span>
        )}
      </div>

      <div
        className="relative rounded-2xl sm:rounded-3xl overflow-hidden border-2 sm:border-4 border-black bg-[#0a0f0d] select-none shadow-none w-full max-w-full sm:shadow-[0_0_24px_rgba(255,47,167,0.25),6px_6px_0_rgba(0,0,0,0.9)]"
        style={{ touchAction: "pan-y" }}
        onPointerDown={onPointerDown}
        onPointerUp={onPointerUp}
      >
        {/* Slide track */}
        <div
          ref={trackRef}
          className="flex transition-transform duration-500 ease-out"
          style={{ transform: `translate3d(-${idx * 100}%, 0, 0)` }}
        >
          {items.map((ep) => (
            <CarouselSlide key={ep.id} ep={ep} />
          ))}
        </div>

        {/* Arrows — hidden on mobile since they overlap the image; swipe +
            dots are the primary controls on touch devices. */}
        {count > 1 && (
          <>
            <button
              type="button"
              onClick={goPrev}
              data-testid="carousel-prev"
              aria-label="Episodul anterior"
              className="hidden sm:flex absolute top-1/2 -translate-y-1/2 left-3 z-10 w-10 h-10 rounded-full bg-[#0a0f0d]/85 border-2 border-[#ff2fa7] text-white items-center justify-center hover:bg-[#ff2fa7] hover:scale-110 transition-all active:scale-95 backdrop-blur-sm"
            >
              <ChevronLeft className="w-5 h-5" strokeWidth={3} />
            </button>
            <button
              type="button"
              onClick={goNext}
              data-testid="carousel-next"
              aria-label="Episodul următor"
              className="hidden sm:flex absolute top-1/2 -translate-y-1/2 right-3 z-10 w-10 h-10 rounded-full bg-[#0a0f0d]/85 border-2 border-[#ff2fa7] text-white items-center justify-center hover:bg-[#ff2fa7] hover:scale-110 transition-all active:scale-95 backdrop-blur-sm"
            >
              <ChevronRight className="w-5 h-5" strokeWidth={3} />
            </button>
          </>
        )}

        {/* Dot navigation */}
        {count > 1 && (
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-10 flex gap-2" data-testid="carousel-dots">
            {dots.map((i) => (
              <button
                key={i}
                type="button"
                onClick={() => setIdx(i)}
                data-testid={`carousel-dot-${i}`}
                aria-label={`Slide ${i + 1}`}
                aria-current={i === idx}
                className={`h-2 rounded-full border-2 border-black transition-all ${
                  i === idx
                    ? "w-8 bg-[#ff2fa7] shadow-[0_0_8px_rgba(255,47,167,0.9)]"
                    : "w-2 bg-white/60 hover:bg-white"
                }`}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function CarouselSlide({ ep }) {
  const thumb = ep.thumbnail_url || ep.series_banner_url || ep.series_poster_url;
  const { primary, secondary } = splitEpisodeTitle(ep.title);
  const bg = ep.series_color || "#ec4899";
  return (
    <Link
      to={`/episod/${ep.id}`}
      className="min-w-full flex-shrink-0 group relative block"
      data-testid={`carousel-slide-${ep.id}`}
      draggable={false}
    >
      <div className="grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)] gap-0">
        {/* Left / top: thumbnail — locked to 16:9 on mobile so images with
            different intrinsic ratios all render consistently. `object-cover`
            fills the box, `object-center` keeps the subject visible. */}
        <div className="relative w-full aspect-[16/9] sm:aspect-auto sm:min-h-[280px] overflow-hidden" style={{ background: bg }}>
          {thumb ? (
            <img
              src={resolveAssetUrl(thumb)}
              alt=""
              draggable={false}
              className="absolute inset-0 w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
            />
          ) : null}
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="w-10 h-10 sm:w-20 sm:h-20 rounded-full bg-[#ff2fa7] border-2 sm:border-4 border-white flex items-center justify-center shadow-[0_0_18px_rgba(255,47,167,0.75)] group-hover:scale-110 transition-transform">
              <Play className="w-4 h-4 sm:w-10 sm:h-10 text-white fill-white ml-0.5" strokeWidth={3} />
            </div>
          </div>
          {ep.series_network && (
            <div className="absolute top-1.5 left-1.5 sm:top-3 sm:left-3 bg-black/70 backdrop-blur-sm border border-white/20 rounded-full px-1.5 py-0.5 text-[9px] sm:text-[10px] font-black uppercase tracking-widest text-white">
              {ep.series_network}
            </div>
          )}
        </div>

        {/* Right / bottom: meta */}
        <div className="p-2.5 sm:p-7 flex flex-col justify-center gap-0.5 sm:gap-2">
          {ep.series_title && (
            <div className="text-[10px] sm:text-xs font-black uppercase tracking-widest text-[#00e5ff] truncate">
              {ep.series_title}
            </div>
          )}
          <div className="font-display font-black text-base sm:text-3xl text-lime-400 text-bubble-sm leading-tight">
            <div className="truncate">{primary}</div>
            {secondary && <div className="truncate">{secondary}</div>}
          </div>
          <div className="text-[9px] sm:text-xs font-black uppercase text-slate-500 tracking-widest truncate">
            S{ep.season} • EP {formatEpisodeNumber(ep.episode_number)}
            {ep.duration && <span className="ml-2">{formatDurationShort(ep.duration)}</span>}
            {formatViews(ep.views) && (
              <span className="inline-flex items-center gap-1">
                {" • "}<Eye className="w-3 h-3 inline" strokeWidth={3} />{formatViews(ep.views)} vizualizări
              </span>
            )}
          </div>
        </div>
      </div>
    </Link>
  );
}
